import { connect } from 'react-redux';
import { GeoTrends } from './GeoTrends';
import selectAndProjectState, { FunctionProps } from './GeoTrends.selectors';
import container from 'src/ServiceContainer';

import {
  cleanUp,
  receiveTenantConfig,
  receiveError,
  receiveGeoData,
  requestGeoData,
  geotrendsRequestChartData,
  geotrendsReceiveChartData,
  TenantResponse,
  selectPoint,
  resetPoint,
  selectGeoLevel,
} from './GeoTrends.slice';

import serviceContainer from 'src/ServiceContainer';
import { TenantConfigViewItem } from 'src/dao/tenantConfigClient';
import { TOP_DOWN } from 'src/utils/Domain/Constants';
import { ExtendedPointObject } from 'src/pages/Hindsighting/MacroTrends/GeoTrends/Charts/SimplerChart';
import { makeScopeSensitive } from 'src/components/higherOrder/ScopeSensitive';
import { getTopMembers } from 'src/pages/Hindsighting/MacroTrends/Summary/Summary.container';
import { AppState, AppThunkDispatch } from 'src/store';
import { AnyAction as BaseAction } from 'redux';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import {
  GeoTrendsMapDefn,
  GeoTrendsChartsDefn,
  zGeoTrendsMapDefn,
  zGeoTrendsChartsDefn,
} from 'configuration-schemas/viewdefns/viewdefn';
import type { GeoTrendsOwnProps } from 'configuration-schemas/ownProps';
import { ListDataApi } from 'configuration-schemas/confdefnView';
import { isNil } from 'lodash';

export function asyncGetMapData(dataApi: ListDataApi, extraAggBy?: string) {
  const service = serviceContainer.pivotService;

  return (dispatch: AppThunkDispatch, getState: () => AppState): Promise<BaseAction | void> => {
    const state = getState();
    const { selectedGeoLevel } = state.pages.hindsighting.geoTrends;
    const { flowStatus = [] } = state.subheader;
    const selectedItem = state.worklist.selectedItemId;

    if (selectedGeoLevel) {
      dispatch(requestGeoData());
      return service
        .listData(dataApi.defnId, dataApi.params?.appName, {
          aggBy: selectedGeoLevel.groupingKey + (extraAggBy ? ',' + extraAggBy : ''),
          flowStatus: flowStatus.join(','),
          topMembers: !isNil(selectedItem) ? selectedItem : getTopMembers(getState().scope.scope),
        })
        .then((resp) => {
          const props = { data: resp, selectedGeoLevel: selectedGeoLevel };
          dispatch(receiveGeoData(props));
        })
        .catch((error) =>
          dispatch(
            receiveError({
              type: ComponentErrorType.data,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.geoTrends,
              stack: (error as Error)?.stack,
              issues: error,
              defnId: error.defnId,
            })
          )
        );
    }
    return Promise.resolve();
  };
}

function asyncGetChartData(dataApi: ListDataApi) {
  const service = serviceContainer.pivotService;

  return (dispatch: AppThunkDispatch, getState: () => AppState): Promise<void> | void => {
    const { selectedItem } = getState().pages.hindsighting.geoTrends;

    if (selectedItem) {
      const itemId = selectedItem.mId;
      dispatch(geotrendsRequestChartData());
      service
        .listData(dataApi.defnId, dataApi.params?.appName, {
          aggBy: dataApi.params?.aggBy ?? '',
          topMembers: itemId,
        })
        .then((resp) => {
          dispatch(geotrendsReceiveChartData(resp.tree));
        })
        .catch((error) =>
          dispatch(
            receiveError({
              type: ComponentErrorType.data,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.geoTrends,
              stack: (error as Error)?.stack,
              issues: error,
              defnId: dataApi.defnId,
            })
          )
        );
    }
  };
}

function dispatchToProps(dispatch: AppThunkDispatch, ownProps: GeoTrendsOwnProps): FunctionProps {
  const client = container.tenantConfigClient;

  return {
    onShowView() {
      const config: TenantResponse = {
        mapConfig: undefined,
        chartConfig: undefined,
        showLookBackPeriod: ownProps.showLookBackPeriod,
        showFlowStatus: ownProps.showFlowStatus,
      };

      client
        .getTenantViewDefns({
          defnIds: [ownProps.defns.view[0], ownProps.defns.view[1]],
          appName: TOP_DOWN,
          validationSchemas: [zGeoTrendsMapDefn, zGeoTrendsChartsDefn],
        })
        .then((resp) => {
          config.mapConfig = (resp[0] as unknown) as GeoTrendsMapDefn;
          config.chartConfig = (resp[1] as unknown) as GeoTrendsChartsDefn;
          dispatch(receiveTenantConfig(config));
          return resp[0];
        })
        .then((mapConfig) => {
          const defaultSelection = mapConfig.default;
          const defaultItem = mapConfig.view.find(
            (item) => item.dataIndex === defaultSelection || item.text === defaultSelection
          );
          if (defaultItem) {
            const selectedIndex = mapConfig.view.indexOf(defaultItem);
            dispatch(selectGeoLevel({ ...defaultItem, selectedIndex }));
          }
        })
        .catch((error) =>
          dispatch(
            receiveError({
              type: ComponentErrorType.config,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.geoTrends,
              stack: (error as Error)?.stack,
              issues: error,
              defnId: error.defnId,
            })
          )
        );
    },
    onUpdate() {},
    // @ts-ignore
    onUpdateGeoLevel(selectedGeo: TenantConfigViewItem) {
      dispatch(selectGeoLevel(selectedGeo));
      dispatch(resetPoint());
    },
    onSelectPoint(selectedItem: ExtendedPointObject) {
      dispatch(selectPoint(selectedItem));
      dispatch(asyncGetChartData(ownProps.graphDataApi));
    },
    onDestroy() {
      dispatch(cleanUp());
    },
  };
}

const scopeSensitiveComponent = makeScopeSensitive(GeoTrends);

export default connect(selectAndProjectState, dispatchToProps)(scopeSensitiveComponent);
